export type Position = [number, number];

export type MapGeometry =
  | { type: "Polygon"; coordinates: Position[][] }
  | { type: "MultiPolygon"; coordinates: Position[][][] };

export type MapFeature = {
  geometry: MapGeometry;
};

export type ViewportSize = {
  width: number;
  height: number;
};

export type MapBounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
};

export type LabelBox = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type LabelLayoutItem = {
  id: string;
  anchor: Position;
  polygonBounds: MapBounds;
  labelWidth: number;
  labelHeight: number;
};

export type LabelPlacement = {
  id: string;
  point: Position;
  box: LabelBox;
  showLeader: boolean;
  leaderEnd: Position;
};

export type FittedProjection = {
  project: (point: Position) => Position;
  geographicBounds: MapBounds;
  projectedBounds: MapBounds;
  scale: number;
  fitRatio: number;
};

export const DEFAULT_MAP_FIT_RATIO = 0.82;
export const DEFAULT_VIEWPORT: ViewportSize = { width: 1100, height: 600 };

const minimumSpan = 0.0001;
const labelEdgeMargin = 10;
const labelGap = 4;

function pointsOf(geometry: MapGeometry): Position[] {
  return geometry.type === "Polygon"
    ? geometry.coordinates.flat()
    : geometry.coordinates.flat(2);
}

export function boundsOf(points: Position[]): MapBounds {
  if (!points.length) {
    return {
      minX: 0,
      maxX: 0,
      minY: 0,
      maxY: 0,
      width: 0,
      height: 0,
    };
  }

  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: Math.max(maxX - minX, 0),
    height: Math.max(maxY - minY, 0),
  };
}

/**
 * Fits every feature through one local equirectangular projection. Longitude
 * is corrected once at the municipality's middle latitude, then the same
 * uniform screen scale is applied to every vertex. No barangay is stretched
 * or fitted independently.
 */
export function createFittedProjection(
  features: MapFeature[],
  viewport: ViewportSize,
  options: { fitRatio?: number; edgePadding?: number } = {},
): FittedProjection | null {
  const points = features.flatMap((feature) => pointsOf(feature.geometry));
  if (!points.length || viewport.width <= 0 || viewport.height <= 0) return null;

  const geographicBounds = boundsOf(points);
  const middleLatitude = (geographicBounds.minY + geographicBounds.maxY) / 2;
  const longitudeCorrection = Math.max(
    0.01,
    Math.cos((middleLatitude * Math.PI) / 180),
  );
  const projectedSpanX = Math.max(
    geographicBounds.width * longitudeCorrection,
    minimumSpan,
  );
  const projectedSpanY = Math.max(geographicBounds.height, minimumSpan);
  const edgePadding = Math.max(0, options.edgePadding ?? 12);
  const requestedFitRatio = options.fitRatio ?? DEFAULT_MAP_FIT_RATIO;
  const fitRatio = Math.min(0.9, Math.max(0.7, requestedFitRatio));
  const usableWidth = Math.max(1, viewport.width - edgePadding * 2);
  const usableHeight = Math.max(1, viewport.height - edgePadding * 2);
  const scale = Math.min(
    (usableWidth * fitRatio) / projectedSpanX,
    (usableHeight * fitRatio) / projectedSpanY,
  );
  const drawnWidth = projectedSpanX * scale;
  const drawnHeight = projectedSpanY * scale;
  const offsetX = (viewport.width - drawnWidth) / 2;
  const offsetY = (viewport.height - drawnHeight) / 2;

  const project = ([longitude, latitude]: Position): Position => [
    offsetX +
      (longitude - geographicBounds.minX) * longitudeCorrection * scale,
    offsetY + (geographicBounds.maxY - latitude) * scale,
  ];

  return {
    project,
    geographicBounds,
    projectedBounds: {
      minX: offsetX,
      maxX: offsetX + drawnWidth,
      minY: offsetY,
      maxY: offsetY + drawnHeight,
      width: drawnWidth,
      height: drawnHeight,
    },
    scale,
    fitRatio,
  };
}

export function estimateLabelSize(lines: string[], fontSize: number) {
  const widths = lines.map((line) => estimateTextWidth(line, fontSize));
  return {
    width: Math.max(28, ...widths) + 10,
    height: Math.max(fontSize + 6, lines.length * fontSize * 1.12 + 7),
  };
}

export function labelFontSize(bounds: MapBounds, viewport: ViewportSize) {
  const compactViewport = viewport.width < 680;
  const base = compactViewport ? 11 : 12;
  const minimumDimension = Math.min(bounds.width, bounds.height);
  const area = bounds.width * bounds.height;
  if (minimumDimension >= 92 && area >= 12_000) return 14;
  if (minimumDimension >= 56 && area >= 5_000) return base + 1;
  return base;
}

function estimateTextWidth(value: string, fontSize: number) {
  let units = 0;
  for (const character of value) {
    if (/[MW@#%&]/.test(character)) units += 0.82;
    else if (/[Iil1'.,:;]/.test(character)) units += 0.32;
    else if (character === " ") units += 0.34;
    else units += 0.61;
  }
  return units * fontSize;
}

export function boxForLabel(
  point: Position,
  labelWidth: number,
  labelHeight: number,
): LabelBox {
  return {
    left: point[0] - labelWidth / 2,
    right: point[0] + labelWidth / 2,
    top: point[1] - labelHeight / 2,
    bottom: point[1] + labelHeight / 2,
  };
}

export function boxesOverlap(a: LabelBox, b: LabelBox, gap = labelGap) {
  return !(
    a.right + gap <= b.left ||
    b.right + gap <= a.left ||
    a.bottom + gap <= b.top ||
    b.bottom + gap <= a.top
  );
}

export function boxInsideViewport(box: LabelBox, viewport: ViewportSize) {
  return (
    box.left >= labelEdgeMargin &&
    box.right <= viewport.width - labelEdgeMargin &&
    box.top >= labelEdgeMargin &&
    box.bottom <= viewport.height - labelEdgeMargin
  );
}

function boxInsideBounds(box: LabelBox, bounds: MapBounds, inset = 3) {
  return (
    box.left >= bounds.minX + inset &&
    box.right <= bounds.maxX - inset &&
    box.top >= bounds.minY + inset &&
    box.bottom <= bounds.maxY - inset
  );
}

function clampLabelPoint(
  point: Position,
  labelWidth: number,
  labelHeight: number,
  viewport: ViewportSize,
): Position {
  const halfWidth = labelWidth / 2;
  const halfHeight = labelHeight / 2;
  return [
    Math.min(
      viewport.width - labelEdgeMargin - halfWidth,
      Math.max(labelEdgeMargin + halfWidth, point[0]),
    ),
    Math.min(
      viewport.height - labelEdgeMargin - halfHeight,
      Math.max(labelEdgeMargin + halfHeight, point[1]),
    ),
  ];
}

function uniquePoints(points: Position[]) {
  const seen = new Set<string>();
  return points.filter(([x, y]) => {
    const key = `${x.toFixed(2)}:${y.toFixed(2)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function candidatePoints(item: LabelLayoutItem, viewport: ViewportSize) {
  const { anchor, polygonBounds, labelWidth, labelHeight } = item;
  const halfWidth = labelWidth / 2;
  const halfHeight = labelHeight / 2;
  const horizontalGap = Math.max(10, Math.min(20, polygonBounds.width * 0.16));
  const verticalGap = Math.max(8, Math.min(18, polygonBounds.height * 0.16));
  const rightX = polygonBounds.maxX + horizontalGap + halfWidth;
  const leftX = polygonBounds.minX - horizontalGap - halfWidth;
  const topY = polygonBounds.minY - verticalGap - halfHeight;
  const bottomY = polygonBounds.maxY + verticalGap + halfHeight;
  const anchorBox = boxForLabel(anchor, labelWidth, labelHeight);
  const fitsInside = boxInsideBounds(anchorBox, polygonBounds);
  const outside: Position[] = [
    [rightX, anchor[1]],
    [leftX, anchor[1]],
    [anchor[0], topY],
    [anchor[0], bottomY],
    [rightX, topY],
    [leftX, topY],
    [rightX, bottomY],
    [leftX, bottomY],
  ];

  const radial: Position[] = [];
  const startRadius = Math.max(
    28,
    Math.min(70, Math.max(labelWidth, labelHeight, polygonBounds.width * 0.4)),
  );
  const maximumRadius = Math.min(
    Math.max(viewport.width, viewport.height) * 0.42,
    260,
  );
  for (let radius = startRadius; radius <= maximumRadius; radius += 22) {
    for (let step = 0; step < 16; step += 1) {
      const angle = (step * Math.PI) / 8;
      radial.push([
        anchor[0] + Math.cos(angle) * radius,
        anchor[1] + Math.sin(angle) * radius,
      ]);
    }
  }

  return uniquePoints(
    (fitsInside ? [anchor, ...outside, ...radial] : [...outside, ...radial, anchor])
      .map((point) =>
        clampLabelPoint(point, labelWidth, labelHeight, viewport),
      ),
  );
}

function scanViewport(item: LabelLayoutItem, viewport: ViewportSize) {
  const points: Position[] = [];
  const xStep = Math.max(item.labelWidth + labelGap * 2, 44);
  const yStep = Math.max(item.labelHeight + labelGap * 2, 28);
  const startX = labelEdgeMargin + item.labelWidth / 2;
  const startY = labelEdgeMargin + item.labelHeight / 2;
  for (
    let y = startY;
    y <= viewport.height - labelEdgeMargin - item.labelHeight / 2;
    y += yStep
  ) {
    for (
      let x = startX;
      x <= viewport.width - labelEdgeMargin - item.labelWidth / 2;
      x += xStep
    ) {
      points.push([x, y]);
    }
  }
  return points.sort(
    (a, b) => distance(a, item.anchor) - distance(b, item.anchor),
  );
}

function distance(a: Position, b: Position) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

function overlapsAny(box: LabelBox, boxes: LabelBox[]) {
  return boxes.some((other) => boxesOverlap(box, other));
}

function leaderEndFor(anchor: Position, point: Position, box: LabelBox): Position {
  const dx = anchor[0] - point[0];
  const dy = anchor[1] - point[1];
  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) return point;

  const halfWidth = (box.right - box.left) / 2;
  const halfHeight = (box.bottom - box.top) / 2;
  const scaleX = Math.abs(dx) > 0.001 ? halfWidth / Math.abs(dx) : Infinity;
  const scaleY = Math.abs(dy) > 0.001 ? halfHeight / Math.abs(dy) : Infinity;
  const scale = Math.min(scaleX, scaleY);
  return [point[0] + dx * scale, point[1] + dy * scale];
}

/**
 * Places every label without hiding any. Compact polygons are handled first;
 * labels search progressively wider rings and finally a viewport-wide packing
 * grid. Obstacles may represent any UI that shares the plot rectangle.
 */
export function layoutMapLabels(
  items: LabelLayoutItem[],
  viewport: ViewportSize,
  obstacles: LabelBox[] = [],
): LabelPlacement[] {
  const placedBoxes: LabelBox[] = [...obstacles];
  const placements = new Map<string, LabelPlacement>();
  const ordered = [...items].sort((a, b) => {
    const aFits = boxInsideBounds(
      boxForLabel(a.anchor, a.labelWidth, a.labelHeight),
      a.polygonBounds,
    );
    const bFits = boxInsideBounds(
      boxForLabel(b.anchor, b.labelWidth, b.labelHeight),
      b.polygonBounds,
    );
    if (aFits !== bFits) return aFits ? 1 : -1;
    return (
      a.polygonBounds.width * a.polygonBounds.height -
      b.polygonBounds.width * b.polygonBounds.height
    );
  });

  for (const item of ordered) {
    const candidates = [
      ...candidatePoints(item, viewport),
      ...scanViewport(item, viewport),
    ];
    let selectedPoint: Position | null = null;
    let selectedBox: LabelBox | null = null;

    for (const candidate of candidates) {
      const box = boxForLabel(candidate, item.labelWidth, item.labelHeight);
      if (!boxInsideViewport(box, viewport) || overlapsAny(box, placedBoxes)) {
        continue;
      }
      selectedPoint = candidate;
      selectedBox = box;
      break;
    }

    if (!selectedPoint || !selectedBox) {
      const fallbackPoint = clampLabelPoint(
        item.anchor,
        item.labelWidth,
        item.labelHeight,
        viewport,
      );
      selectedPoint = fallbackPoint;
      selectedBox = boxForLabel(
        fallbackPoint,
        item.labelWidth,
        item.labelHeight,
      );
    }

    const showLeader =
      !boxInsideBounds(selectedBox, item.polygonBounds, 0) ||
      distance(selectedPoint, item.anchor) > 18;
    placements.set(item.id, {
      id: item.id,
      point: selectedPoint,
      box: selectedBox,
      showLeader,
      leaderEnd: leaderEndFor(item.anchor, selectedPoint, selectedBox),
    });
    placedBoxes.push(selectedBox);
  }

  return items.map((item) => {
    const placement = placements.get(item.id);
    if (placement) return placement;
    const point = clampLabelPoint(
      item.anchor,
      item.labelWidth,
      item.labelHeight,
      viewport,
    );
    const box = boxForLabel(point, item.labelWidth, item.labelHeight);
    return {
      id: item.id,
      point,
      box,
      showLeader: true,
      leaderEnd: leaderEndFor(item.anchor, point, box),
    };
  });
}

/** At a selected view, show nearby labels only where their own anchors fit. */
export function layoutFocusedMapLabels(
  items: LabelLayoutItem[],
  viewport: ViewportSize,
  selectedId: string,
): LabelPlacement[] {
  const placedBoxes: LabelBox[] = [];
  const placements: LabelPlacement[] = [];
  const ordered = [...items].sort((a, b) =>
    (a.id === selectedId ? -1 : b.id === selectedId ? 1 :
      b.polygonBounds.width * b.polygonBounds.height -
      a.polygonBounds.width * a.polygonBounds.height),
  );

  for (const item of ordered) {
    const box = boxForLabel(item.anchor, item.labelWidth, item.labelHeight);
    if (!boxInsideViewport(box, viewport) || overlapsAny(box, placedBoxes)) continue;
    placements.push({
      id: item.id,
      point: item.anchor,
      box,
      showLeader: false,
      leaderEnd: item.anchor,
    });
    placedBoxes.push(box);
  }

  return placements;
}

export function zoomTransform(
  zoom: number,
  focusPoint: Position,
  viewport: ViewportSize,
) {
  if (zoom === 1) return undefined;
  return `translate(${viewport.width / 2} ${viewport.height / 2}) scale(${zoom}) translate(${-focusPoint[0]} ${-focusPoint[1]})`;
}
